/**
 * Fait : la fiche d'un projet — son nom, ses membres, ses conversations,
 *   QUI EST PRÉSENT en direct par le canal, et le partage. Ce que l'écran
 *   permet découle du niveau rendu par l'API. Seconde voie du critère 14.
 *
 * Ne fait pas : elle ne réécrit aucune règle de droit et ne devine rien du
 *   canal — elle écoute `presence` et `evenement_projet`, et recharge quand
 *   le serveur le lui dit.
 */

import { useEffect, useRef, useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { creeUnFil, listeLesFils, type FilVu } from './api-fil.ts';
import { ecritLeProjet } from './arbitrage-du-projet.ts';
import { ecouteLeProjet } from './canal-du-projet.ts';
import { useFraicheur } from './fraicheur.ts';
import { SectionDocuments } from './SectionDocuments.tsx';
import { SectionFicheDuProjet } from './SectionFicheDuProjet.tsx';
import { SectionFils } from './SectionFils.tsx';
import { PartieDesParticipants } from './PartieDesParticipants.tsx';
import { type Issue, type MembreVu, type ProjetVu } from './api.ts';
import {
  archiveLeProjet,
  listeLesMembres,
  listeLesProjets,
  metLeProjetALaCorbeille,
  renommeLeProjet,
} from './api-projet.ts';

export function PageProjet({
  projetId,
  moiId,
}: {
  readonly projetId: string;

  /** Mon propre identifiant : la fiche doit reconnaître MA ligne. */
  readonly moiId: string;
}) {
  const [projet, setProjet] = useState<ProjetVu | null>(null);
  const [membres, setMembres] = useState<MembreVu[]>([]);
  const [fils, setFils] = useState<FilVu[]>([]);
  const [titreDeFil, setTitreDeFil] = useState('');
  const [presents, setPresents] = useState<string[]>([]);
  const [echec, setEchec] = useState<string | null>(null);

  /** Ce qui manque quand la fiche s'affiche quand même — jamais un vide muet. */
  const [partiel, setPartiel] = useState<string | null>(null);

  const [lienCoupe, setLienCoupe] = useState(false);

  /** Change à chaque événement du projet : c'est le réveil de la partie documents. */
  const [tic, setTic] = useState(0);

  /*
   * A-t-on DÉJÀ vu ce projet à l'écran ? Il faut une référence, et pas l'état :
   * le rappel branché sur le canal est capturé UNE fois par l'effet ci-dessous,
   * et lirait éternellement le `projet` du premier rendu, c'est-à-dire `null`.
   * De quoi annoncer « ce projet n'existe pas » à quelqu'un qui le regarde.
   */
  const dejaVu = useRef(false);

  // Réponse périmée : voir `fraicheur.ts`, qui porte la règle et sa raison.
  const fraicheur = useFraicheur(projetId);

  /** Recharge la fiche : le projet, ses membres, ses fils. */
  async function recharge(): Promise<void> {
    const pour = fraicheur.demandee;
    const [tousLesProjets, sesMembres, sesFils] = await Promise.all([
      listeLesProjets(),
      listeLesMembres(pour),
      listeLesFils(pour),
    ]);

    if (!fraicheur.encoreValable(pour)) return;

    if (sesFils.bon) setFils(sesFils.valeur.conversations);

    // Une liste qui n'a pas pu être chargée n'est PAS une liste vide. Les
    // confondre — ce que faisait la version d'avant — fait dire à l'écran
    // « aucun membre », « aucune conversation », alors qu'il n'en sait rien.
    setPartiel(
      sesFils.bon && sesMembres.bon
        ? null
        : 'Une partie de cette fiche n’a pas pu être chargée. Réessayez dans un instant.',
    );

    if (!tousLesProjets.bon) {
      setEchec(tousLesProjets.erreur.message);
      return;
    }

    const trouve = tousLesProjets.valeur.find((ligne) => ligne.id === pour);
    if (trouve === undefined) {
      /*
       * LE DIRECT QUAND LE PROJET DISPARAÎT. Le canal nous a réveillés, la
       * liste ne le rend plus : quelqu'un vient de le mettre à la corbeille,
       * ou notre accès a été retiré. On EFFACE la fiche au lieu de poser un
       * bandeau au-dessus d'un projet qui a l'air encore là.
       */
      setProjet(null);
      setEchec(
        dejaVu.current
          ? 'Ce projet vient de disparaître : il a été supprimé, ou votre accès a été retiré.'
          : 'Ce projet n’existe pas, ou ne vous est pas partagé.',
      );
      return;
    }

    dejaVu.current = true;
    setProjet(trouve);
    setMembres(sesMembres.bon ? sesMembres.valeur : []);
    setEchec(null);
  }

  useEffect(() => {
    // Changer de projet remet la fiche à ZÉRO : l'ancien restait sinon
    // affiché SOUS le message d'erreur du nouveau (audit de la phase 4).
    setProjet(null);
    setMembres([]);
    setFils([]);
    setPresents([]);
    setEchec(null);
    setLienCoupe(false);
    dejaVu.current = false;

    void recharge();

    return ecouteLeProjet(
      projetId,
      (recus) => setPresents(recus),
      () => {
        // Le direct réveille AUSSI les documents : leurs états changent seuls,
        // en tâche de fond, et « Lecture en cours » resterait sinon affiché.
        setTic((tour) => tour + 1);
        void recharge();
      },
      () => setLienCoupe(true),
    );
  }, [projetId]);

  /**
   * Renomme le projet. Nom et description partent ENSEMBLE : une seule
   * demande, un seul arbitrage. Les séparer donnerait deux écritures
   * concurrentes pour une modification que l'utilisateur croit unique.
   */
  async function modifie(nom: string, description: string): Promise<void> {
    if (projet === null) return;

    await ecritEtRecharge(projet.version, (version) =>
      renommeLeProjet(projetId, nom, version, description),
    );
  }

  /** Archive ou désarchive — même arbitrage par version que le renommage. */
  async function archive(veutArchiver: boolean): Promise<void> {
    if (projet === null) return;

    await ecritEtRecharge(projet.version, (version) =>
      archiveLeProjet(projetId, veutArchiver, version),
    );
  }

  /** Écrit sous arbitrage de version, montre le refus s'il y en a un, recharge. */
  async function ecritEtRecharge(
    version: number,
    envoie: (version: number) => Promise<Issue<void>>,
  ): Promise<void> {
    const issue = await ecritLeProjet(projetId, version, envoie);

    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    await recharge();
  }

  /**
   * Met le projet à la corbeille, puis QUITTE la fiche : elle n'a plus d'objet.
   * Les autres onglets, eux, l'apprennent par le direct.
   *
   * Le refus du serveur est MONTRÉ : si quelqu'un a supprimé le projet entre
   * l'ouverture de la fenêtre et le clic de confirmation, la réponse n'est plus
   * « c'est fait » — elle dit qu'on a été devancé, et l'écran le répète.
   */
  async function supprime(): Promise<void> {
    const issue = await metLeProjetALaCorbeille(projetId);

    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    location.hash = '#/projets';
  }

  if (echec !== null && projet === null) {
    return (
      <section data-etat="erreur">
        <h2>Ce projet ne s’ouvre pas</h2>
        <Annonce urgence="urgent">{echec}</Annonce>
        <a href="#/projets">Revenir à mes projets</a>
      </section>
    );
  }

  if (projet === null) {
    return (
      <section data-etat="chargement" aria-busy="true">
        <h2>Ouverture du projet…</h2>
      </section>
    );
  }

  return (
    <div
      className="empilement"
      data-etat="nominal"
      data-projet={projet.id}
      data-niveau={projet.niveau}
    >
      {lienCoupe && (
        <Annonce urgence="urgent">
          Le lien avec l’atelier est coupé. Rechargez la page pour reprendre.
        </Annonce>
      )}

      {partiel !== null && (
        <span data-role="chargement-partiel">
          <Annonce urgence="urgent">{partiel}</Annonce>
        </span>
      )}

      {echec !== null && (
        <span data-role="echec">
          <Annonce urgence="urgent">{echec}</Annonce>
        </span>
      )}

      <SectionFicheDuProjet
        projet={projet}
        presents={presents.length}
        peutModifier={projet.niveau !== 'lecteur' && !projet.archive}
        // Archiver, désarchiver et supprimer administrent le projet : le
        // serveur les réserve au propriétaire, l'écran montre la même chose.
        // Et un projet archivé se désarchive et se supprime — sans quoi on
        // enfermerait le propriétaire dans un projet qu'il ne peut plus quitter.
        peutAdministrer={projet.niveau === 'proprietaire'}
        surModification={modifie}
        surArchivage={(veutArchiver) => void archive(veutArchiver)}
        surSuppression={() => void supprime()}
      />

      <SectionFils
        projetId={projet.id}
        fils={fils}
        peutCreer={projet.niveau !== 'lecteur' && !projet.archive}
        titre={titreDeFil}
        surTitre={setTitreDeFil}
        surCreation={() => {
          void (async () => {
            const cree = await creeUnFil(projet.id, titreDeFil.trim());
            if (cree.bon) {
              setTitreDeFil('');
              location.hash = `#/projets/${projet.id}/fils/${cree.valeur.conversationId}`;
            } else {
              setEchec(cree.erreur.message);
            }
          })();
        }}
      />

      <SectionDocuments
        projetId={projet.id}
        peutModifier={projet.niveau !== 'lecteur' && !projet.archive}
        tic={tic}
      />

      <PartieDesParticipants
        projet={projet}
        membres={membres}
        presents={presents}
        moiId={moiId}
        surEchec={setEchec}
        recharge={recharge}
      />

      <p>
        <a href="#/projets">Revenir à mes projets</a>
      </p>
    </div>
  );
}
